import {
  AccessPolicy,
  AccessPolicyResource,
  ProjectMembership,
} from "@medplum/fhirtypes";
import { captureException } from "@sentry/react";

import { CalendarType } from "@/types/scheduling";
import { MemberRole, Role, UserAccess } from "@/types/users";
import { FHIR_CODES } from "@/constants/fhir.constants";

import { isNullable } from "./utils";

const ROLE_ACCESS_OVERRIDES: Partial<Record<MemberRole, Partial<UserAccess>>> =
  {
    [Role.Admin]: {
      canViewAnalytics: true,
      canManageWhatsApp: true,
      canManageClinicConfig: true,
    },
  };

const buildAccess = (
  allowed: boolean,
  options?: Partial<UserAccess>
): UserAccess => ({
  canEditProject: options?.canEditProject ?? allowed,
  canManageClinicConfig: options?.canManageClinicConfig ?? allowed,
  canReadMembers: options?.canReadMembers ?? allowed,
  canWriteMembers: options?.canWriteMembers ?? allowed,
  canManageAllPractitioners: options?.canManageAllPractitioners ?? allowed,
  canManageOwnPractitioner: options?.canManageOwnPractitioner ?? allowed,
  canConnectOwnCalendar: options?.canConnectOwnCalendar ?? allowed,
  canManageAllSchedules: options?.canManageAllSchedules ?? allowed,
  canManageOwnSchedule: options?.canManageOwnSchedule ?? allowed,
  canManageWhatsApp: options?.canManageWhatsApp ?? allowed,
  canViewAnalytics: options?.canViewAnalytics ?? allowed,
});

export const NO_ACCESS: UserAccess = buildAccess(false);

export const SUPER_ADMIN_ACCESS: UserAccess = buildAccess(true);

type Interaction = NonNullable<AccessPolicyResource["interaction"]>[number];

class AccessPolicyReader {
  private static readonly WRITE_INTERACTIONS = new Set<Interaction>([
    "create",
    "update",
    "delete",
  ]);

  constructor(private readonly policy: AccessPolicy | undefined) {}

  private static isWritable(resource: AccessPolicyResource): boolean {
    const { interaction } = resource;

    return (
      !interaction ||
      interaction.some((i) => AccessPolicyReader.WRITE_INTERACTIONS.has(i))
    );
  }

  /**
   * Parses a FHIR criteria string (e.g. `Basic?code=foo&subject=%profile`)
   * into its query params.
   */
  private static parseCriteriaParams(
    criteria: string | undefined
  ): URLSearchParams {
    const [, query = ""] = (criteria ?? "").split("?");

    return new URLSearchParams(query);
  }

  /**
   * Checks that every key–value pair in `expectedCriteria` appears in
   * `policyCriteria`. Supports any FHIR search parameter — not just `code` —
   * so criteria like `"subject=Patient/123"` or `"actor=%profile"` can be
   * matched exactly. Exact equality is required to avoid over-granting
   * access to overlapping criteria.
   */
  private static matchesFullCriteria(
    policyCriteria: string | undefined,
    expectedCriteria: string | undefined
  ): boolean {
    if (expectedCriteria === undefined) {
      return true;
    }

    if (!policyCriteria) {
      return false;
    }

    const policyParams = AccessPolicyReader.parseCriteriaParams(policyCriteria);
    const expectedParams = new URLSearchParams(expectedCriteria);

    for (const [key, value] of policyParams) {
      if (!expectedParams.getAll(key).includes(value)) {
        return false;
      }
    }

    return true;
  }

  hasRead(resourceType: string, criteria?: string): boolean {
    return Boolean(
      this.policy?.resource?.some(
        ({ resourceType: _resourceType, interaction, criteria: _criteria }) =>
          _resourceType === resourceType &&
          (!interaction || interaction.includes("read")) &&
          AccessPolicyReader.matchesFullCriteria(_criteria, criteria)
      )
    );
  }

  hasReadWrite(resourceType: string, criteria?: string): boolean {
    return Boolean(
      this.policy?.resource?.some(
        (resource) =>
          resource.resourceType === resourceType &&
          AccessPolicyReader.isWritable(resource) &&
          AccessPolicyReader.matchesFullCriteria(resource.criteria, criteria)
      )
    );
  }

  /**
   * A restricted resource has a criteria expression that limits the scope of
   * access to a subset of resources (e.g. only the practitioner's own records).
   * Write access on a restricted resource does not imply full write access to
   * that resource type, so hasReadWriteAll must exclude these.
   */
  private static isRestricted(resource: AccessPolicyResource): boolean {
    return Boolean(resource.criteria);
  }

  hasReadWriteAll(resourceType: string): boolean {
    return Boolean(
      this.policy?.resource?.some(
        (resource) =>
          resource.resourceType === resourceType &&
          AccessPolicyReader.isWritable(resource) &&
          !AccessPolicyReader.isRestricted(resource)
      )
    );
  }

  deriveAccess(overrides?: Partial<UserAccess>): UserAccess {
    return {
      ...buildAccess(false),
      canEditProject: this.hasReadWrite("Project"),
      canReadMembers: this.hasRead("Basic", `code=${FHIR_CODES.MEMBER}`),
      canWriteMembers: this.hasRead("Bot"),
      canManageAllPractitioners: this.hasReadWriteAll("Practitioner"),
      canManageOwnPractitioner: this.hasReadWrite("Practitioner"),
      canConnectOwnCalendar:
        this.hasReadWrite("Basic", `code=${FHIR_CODES.EHR_CONNECTION}`) ||
        this.hasReadWrite(
          "Basic",
          `_id=%ehrConnection&code=${FHIR_CODES.EHR_CONNECTION_TYPE}|${CalendarType.Google}`
        ),
      canManageAllSchedules: this.hasReadWriteAll("Schedule"),
      canManageOwnSchedule: this.hasReadWrite("Schedule"),
      ...overrides,
    };
  }
}

const MEMBER_ROLES = new Set<string>([
  Role.Admin,
  Role.PatientCoordinator,
  Role.Doctor,
]);

export const parseMemberRole = (
  value: string | undefined
): MemberRole | undefined => {
  if (!isNullable(value) && MEMBER_ROLES.has(value)) {
    return value as MemberRole;
  }

  return undefined;
};

const warnAdminMembership = (membershipId: string | undefined) => {
  captureException(
    new Error(
      `ProjectMembership ${membershipId} has admin: true for a non-super-admin user`
    )
  );
};

export const buildUserAccess = (
  isSuperAdmin: boolean,
  membership: ProjectMembership,
  accessPolicy: AccessPolicy | undefined,
  memberRole?: MemberRole
): UserAccess => {
  if (isSuperAdmin) {
    return SUPER_ADMIN_ACCESS;
  }

  if (membership.admin) {
    warnAdminMembership(membership.id);
  }

  const overrides = memberRole ? ROLE_ACCESS_OVERRIDES[memberRole] : undefined;

  return new AccessPolicyReader(accessPolicy).deriveAccess(overrides);
};

export const deriveRole = (
  isSuperAdmin: boolean,
  membership: ProjectMembership,
  memberRole: string | undefined
): Role => {
  if (isSuperAdmin) {
    return Role.SuperAdmin;
  }

  if (membership.admin) {
    warnAdminMembership(membership.id);
  }

  const role = parseMemberRole(memberRole);

  if (!role) {
    throw new Error(`Membership ${membership.id} has no recognized role`);
  }

  return role;
};
